import React, { useState } from 'react';
import {
  Mail,
  User,
  AtSign,
  ShieldCheck,
  Trash2,
  CheckCircle2,
  Clock,
  Send,
  Info
} from 'lucide-react';
import { useLanguage } from '../i18n';

const ExclusaoDados = () => {
  const { language } = useLanguage();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    requestType: 'all',
    message: ''
  });

  const isPT = language === 'pt';

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const requestLabels = {
      all: isPT
        ? 'Eliminar todos os meus dados e conta'
        : 'Delete all my data and account',
      account: isPT
        ? 'Eliminar apenas a minha conta'
        : 'Delete only my account',
      personal: isPT
        ? 'Eliminar os meus dados pessoais'
        : 'Delete my personal data',
      other: isPT
        ? 'Outro pedido relacionado com os meus dados'
        : 'Other request related to my data'
    };

    const subject = isPT
      ? `Pedido de Exclusão de Dados - ${formData.name}`
      : `Data Deletion Request - ${formData.name}`;

    const body = isPT
      ? `Olá,

Pretendo solicitar a exclusão dos meus dados pessoais associados à Lou's Way.

Nome: ${formData.name}
E-mail associado à conta: ${formData.email}
Tipo de pedido: ${requestLabels[formData.requestType]}

Informações adicionais:
${formData.message || 'Sem informações adicionais.'}

Solicito que entrem em contacto comigo através deste e-mail caso seja necessária alguma informação adicional para confirmar a minha identidade ou processar o pedido.

Obrigado.`
      : `Hello,

I would like to request the deletion of my personal data associated with Lou's Way.

Name: ${formData.name}
Email associated with the account: ${formData.email}
Request type: ${requestLabels[formData.requestType]}

Additional information:
${formData.message || 'No additional information.'}

Please contact me through this email address if any additional information is required to verify my identity or process this request.

Thank you.`;

    const mailto = `mailto:lourdyane.rod@gmail.com?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    window.location.href = mailto;
  };

  const styles = {
    page: {
      minHeight: '100vh',
      background:
        'linear-gradient(180deg, #F8F5EF 0%, #F3EEE5 50%, #F8F5EF 100%)',
      color: '#2C2924'
    },
    container: {
      maxWidth: '1180px',
      margin: '0 auto',
      padding: '80px 24px 100px'
    },
    hero: {
      textAlign: 'center',
      maxWidth: '780px',
      margin: '0 auto 64px'
    },
    eyebrow: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#9B7B3E',
      fontSize: '12px',
      fontWeight: '600',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      marginBottom: '18px'
    },
    title: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 'clamp(36px, 6vw, 64px)',
      fontWeight: '400',
      lineHeight: '1.08',
      color: '#2C2924',
      margin: '0 0 22px'
    },
    subtitle: {
      maxWidth: '680px',
      margin: '0 auto',
      fontSize: '16px',
      lineHeight: '1.8',
      color: '#716A60'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: '32px',
      alignItems: 'start'
    },
    card: {
      background: 'rgba(255, 255, 255, 0.78)',
      border: '1px solid #E6DED1',
      borderRadius: '24px',
      padding: '32px',
      boxShadow: '0 18px 50px rgba(70, 57, 38, 0.06)'
    },
    cardTitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '28px',
      fontWeight: '400',
      color: '#2C2924',
      margin: '0 0 12px'
    },
    cardText: {
      color: '#716A60',
      fontSize: '14px',
      lineHeight: '1.75',
      margin: '0 0 28px'
    },
    field: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '13px',
      fontWeight: '600',
      color: '#4B463E',
      marginBottom: '8px'
    },
    inputWrapper: {
      position: 'relative'
    },
    inputIcon: {
      position: 'absolute',
      left: '15px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#BFA05F',
      pointerEvents: 'none'
    },
    input: {
      width: '100%',
      boxSizing: 'border-box',
      border: '1px solid #DED5C7',
      borderRadius: '12px',
      background: '#FFFFFF',
      padding: '14px 16px 14px 45px',
      fontSize: '14px',
      color: '#2C2924',
      outline: 'none'
    },
    select: {
      width: '100%',
      boxSizing: 'border-box',
      border: '1px solid #DED5C7',
      borderRadius: '12px',
      background: '#FFFFFF',
      padding: '14px 16px',
      fontSize: '14px',
      color: '#2C2924',
      outline: 'none'
    },
    textarea: {
      width: '100%',
      boxSizing: 'border-box',
      minHeight: '130px',
      resize: 'vertical',
      border: '1px solid #DED5C7',
      borderRadius: '12px',
      background: '#FFFFFF',
      padding: '14px 16px',
      fontSize: '14px',
      lineHeight: '1.6',
      color: '#2C2924',
      outline: 'none',
      fontFamily: 'inherit'
    },
    button: {
      width: '100%',
      border: 'none',
      borderRadius: '12px',
      background: '#1A1815',
      color: '#FFFFFF',
      padding: '15px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px'
    },
    privacyNote: {
      display: 'flex',
      gap: '10px',
      alignItems: 'flex-start',
      marginTop: '18px',
      color: '#8A8175',
      fontSize: '12px',
      lineHeight: '1.6'
    },
    steps: {
      display: 'flex',
      flexDirection: 'column',
      gap: '22px',
      marginTop: '28px'
    },
    step: {
      display: 'flex',
      gap: '16px',
      alignItems: 'flex-start'
    },
    iconBox: {
      width: '44px',
      height: '44px',
      minWidth: '44px',
      borderRadius: '50%',
      background: '#F1E8D8',
      color: '#A17D3D',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    stepTitle: {
      margin: '1px 0 5px',
      color: '#38332D',
      fontSize: '15px',
      fontWeight: '600'
    },
    stepText: {
      margin: 0,
      color: '#777065',
      fontSize: '13px',
      lineHeight: '1.7'
    },
    notice: {
      marginTop: '28px',
      background: '#F6F0E5',
      border: '1px solid #E5D6BA',
      borderRadius: '16px',
      padding: '20px',
      display: 'flex',
      gap: '14px',
      alignItems: 'flex-start'
    },
    noticeText: {
      margin: 0,
      color: '#6D6253',
      fontSize: '13px',
      lineHeight: '1.7'
    },
    directContact: {
      marginTop: '24px',
      paddingTop: '24px',
      borderTop: '1px solid #E8E0D5'
    },
    emailLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#9B7B3E',
      fontSize: '14px',
      fontWeight: '600',
      textDecoration: 'none',
      marginTop: '8px'
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <section style={styles.hero}>
          <div style={styles.eyebrow}>
            <ShieldCheck size={16} />
            {isPT ? 'Privacidade e proteção de dados' : 'Privacy and data protection'}
          </div>

          <h1 style={styles.title}>
            {isPT ? 'Exclusão de Dados do Utilizador' : 'User Data Deletion'}
          </h1>

          <p style={styles.subtitle}>
            {isPT
              ? "Na Lou's Way respeitamos a tua privacidade. Se pretendes eliminar a tua conta ou solicitar a exclusão dos teus dados pessoais, podes enviar-nos o teu pedido através do formulário abaixo."
              : "At Lou's Way we respect your privacy. If you wish to delete your account or request the deletion of your personal data, you can submit your request using the form below."}
          </p>
        </section>

        <div style={styles.grid}>
          {/* Formulário */}
          <section style={styles.card}>
            <h2 style={styles.cardTitle}>
              {isPT ? 'Solicitar exclusão' : 'Request deletion'}
            </h2>

            <p style={styles.cardText}>
              {isPT
                ? 'Preenche os dados abaixo para que possamos identificar a tua conta e analisar o pedido.'
                : 'Fill in the information below so we can identify your account and review your request.'}
            </p>

            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="name">
                  {isPT ? 'Nome completo' : 'Full name'}
                </label>

                <div style={styles.inputWrapper}>
                  <User
                    size={18}
                    style={styles.inputIcon}
                  />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={isPT ? 'O teu nome' : 'Your name'}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="email">
                  {isPT ? 'E-mail associado à conta' : 'Email associated with the account'}
                </label>

                <div style={styles.inputWrapper}>
                  <AtSign
                    size={18}
                    style={styles.inputIcon}
                  />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="exemplo@email.com"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="requestType">
                  {isPT ? 'O que pretendes eliminar?' : 'What would you like to delete?'}
                </label>

                <select
                  id="requestType"
                  name="requestType"
                  value={formData.requestType}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="all">
                    {isPT
                      ? 'Todos os meus dados e a minha conta'
                      : 'All my data and my account'}
                  </option>
                  <option value="account">
                    {isPT
                      ? 'Apenas a minha conta'
                      : 'Only my account'}
                  </option>
                  <option value="personal">
                    {isPT
                      ? 'Os meus dados pessoais'
                      : 'My personal data'}
                  </option>
                  <option value="other">
                    {isPT
                      ? 'Outro pedido relacionado com os meus dados'
                      : 'Another request related to my data'}
                  </option>
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="message">
                  {isPT
                    ? 'Informações adicionais (opcional)'
                    : 'Additional information (optional)'}
                </label>

                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={
                    isPT
                      ? 'Se necessário, explica aqui o teu pedido...'
                      : 'If necessary, explain your request here...'
                  }
                  style={styles.textarea}
                />
              </div>

              <button type="submit" style={styles.button}>
                <Send size={17} />
                {isPT ? 'Enviar pedido de exclusão' : 'Send deletion request'}
              </button>

              <div style={styles.privacyNote}>
                <ShieldCheck size={16} style={{ minWidth: '16px' }} />
                <span>
                  {isPT
                    ? 'As informações introduzidas neste formulário serão utilizadas apenas para identificar e tratar o teu pedido.'
                    : 'The information entered in this form will only be used to identify and process your request.'}
                </span>
              </div>
            </form>
          </section>

          {/* Informação */}
          <section style={styles.card}>
            <h2 style={styles.cardTitle}>
              {isPT ? 'Como funciona?' : 'How does it work?'}
            </h2>

            <p style={styles.cardText}>
              {isPT
                ? 'Queremos que o processo seja simples e transparente.'
                : 'We want the process to be simple and transparent.'}
            </p>

            <div style={styles.steps}>
              <div style={styles.step}>
                <div style={styles.iconBox}>
                  <Send size={19} />
                </div>
                <div>
                  <h3 style={styles.stepTitle}>
                    {isPT ? '1. Envia o pedido' : '1. Submit your request'}
                  </h3>
                  <p style={styles.stepText}>
                    {isPT
                      ? 'Preenche o formulário com o e-mail associado à tua conta Lou’s Way.'
                      : 'Complete the form using the email associated with your Lou’s Way account.'}
                  </p>
                </div>
              </div>

              <div style={styles.step}>
                <div style={styles.iconBox}>
                  <ShieldCheck size={19} />
                </div>
                <div>
                  <h3 style={styles.stepTitle}>
                    {isPT ? '2. Verificação' : '2. Verification'}
                  </h3>
                  <p style={styles.stepText}>
                    {isPT
                      ? 'Poderemos entrar em contacto contigo para confirmar a tua identidade antes de eliminar os dados.'
                      : 'We may contact you to confirm your identity before deleting your data.'}
                  </p>
                </div>
              </div>

              <div style={styles.step}>
                <div style={styles.iconBox}>
                  <Trash2 size={19} />
                </div>
                <div>
                  <h3 style={styles.stepTitle}>
                    {isPT ? '3. Tratamento do pedido' : '3. Request processing'}
                  </h3>
                  <p style={styles.stepText}>
                    {isPT
                      ? 'Após a validação, analisaremos quais os dados que podem ser eliminados e procederemos à sua exclusão.'
                      : 'After validation, we will determine which data can be deleted and proceed with its deletion.'}
                  </p>
                </div>
              </div>

              <div style={styles.step}>
                <div style={styles.iconBox}>
                  <CheckCircle2 size={19} />
                </div>
                <div>
                  <h3 style={styles.stepTitle}>
                    {isPT ? '4. Confirmação' : '4. Confirmation'}
                  </h3>
                  <p style={styles.stepText}>
                    {isPT
                      ? 'Receberás uma resposta quando o teu pedido tiver sido tratado ou caso seja necessária informação adicional.'
                      : 'You will receive a response when your request has been processed or if additional information is required.'}
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.notice}>
              <Info
                size={20}
                color="#A17D3D"
                style={{ minWidth: '20px', marginTop: '2px' }}
              />
              <p style={styles.noticeText}>
                {isPT
                  ? 'O direito ao apagamento não é absoluto. Alguns dados poderão ter de ser conservados quando exista uma obrigação legal, fiscal, contabilística ou outra base legítima que imponha a sua conservação.'
                  : 'The right to erasure is not absolute. Certain data may need to be retained where there is a legal, tax, accounting or other legitimate obligation requiring its retention.'}
              </p>
            </div>

            <div style={styles.directContact}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#4B463E',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                <Mail size={17} color="#BFA05F" />
                {isPT ? 'Preferes contactar-nos diretamente?' : 'Prefer to contact us directly?'}
              </div>

              <a
                href="mailto:lourdyane.rod@gmail.com?subject=Pedido%20de%20Exclus%C3%A3o%20de%20Dados%20-%20Lou%27s%20Way"
                style={styles.emailLink}
              >
                lourdyane.rod@gmail.com
              </a>
            </div>
          </section>
        </div>

        <div
          style={{
            textAlign: 'center',
            marginTop: '42px',
            color: '#8A8175',
            fontSize: '13px',
            lineHeight: '1.7'
          }}
        >
          <Clock
            size={15}
            style={{
              display: 'inline-block',
              verticalAlign: 'middle',
              marginRight: '7px'
            }}
          />
          {isPT
            ? 'Os pedidos serão tratados de acordo com a legislação aplicável em matéria de proteção de dados.'
            : 'Requests will be handled in accordance with applicable data protection legislation.'}
        </div>
      </div>
    </div>
  );
};

export default ExclusaoDados;